import { AnalyticsEvents, track } from 'v2/utils/analytics'
import { logger } from 'v2/utils/logger'
import { errorToast } from 'v2/utils/toasts'
import { useCashfreeCreateOrderMutation } from '../queries'
import { apiErrorMessage, launchCashfreeHostedCheckout } from '../recharge-shared'
import { getAccountId } from '../utils'
import { runCashfreeNetbankingCheckout } from './cashfree-netbanking-checkout'
import { useLaunchGuard } from './use-launch-guard'

// Netbanking always runs on CASHFREE, whichever gateway the org itself resolves
// to. Razorpay does not support netbanking, so a Razorpay INR account still pays
// by netbanking through Cashfree's hosted page.
//
// The consumer is razorpay-custom-recharge-modal (the Standard variant that also
// used this was deleted when Razorpay went GA), which would otherwise spell the
// cross-gateway call itself.
//
// The Payment Modes netbanking tile is NOT one of them: it renders
// CashfreeRechargeModal directly with restrictToMethod="netbanking", and that
// component builds its own order and owns its own isLaunchingCheckout state. It
// is already on Cashfree by construction, so it has nothing to route — but it is
// a genuinely separate implementation, not something this hook keeps in line.
//
// The backend allows the cross-gateway call: CreateCashfreeOrder gates on
// IsCashfreeEnabled(), a service-wide config flag, not on the account's gateway
// (payment-service usecases/cashfree_payment.go:253).
//
// This is a FULL-PAGE redirect (`_self`), not an in-page modal: the customer
// leaves the SPA for Cashfree's page and returns via /payment/callback. Any
// dialog hosting this is destroyed on navigation, so do NOT follow the call with
// state updates or a success toast — nothing after it runs.
//
// `isLaunchingNetbanking` therefore spans the WHOLE attempt — that is useLaunchGuard,
// which latches on success and releases on failure, and is unit-tested there. It
// cannot be the create-order mutation's `isPending`: that clears the moment the
// order resolves, while launchCashfreeHostedCheckout may still be fetching
// Cashfree.js (`load()` in cashfree-sdk.ts — a real network round trip on a cold
// session) and awaiting checkout(). Both consumers fold this flag into the CTA's
// disabled state, so a gap there re-enables the button and a second click creates
// a SECOND Cashfree order for the same recharge.
//
// Deliberately thin: the decisions live in runCashfreeNetbankingCheckout, which
// is injectable and unit-tested. This wrapper only supplies the real
// collaborators and turns a failure into a message.

interface LaunchNetbankingParams {
  amount: number
  // Funnel labelling only — the caller's own screen, so an event fired from the
  // Razorpay modal is not mistaken for one fired from the Cashfree modal.
  source: string
  // Reported on RECHARGE_INITIATED, AND carried into the order as upgrade_plan so
  // a trial's first netbanking payment flips the plan on the capture webhook.
  isTrial?: boolean
}

const FAILURE_MESSAGE = 'Could not start netbanking. Please try again.'

export const useCashfreeNetbankingCheckout = () => {
  const { mutateAsync: createCashfreeOrder } = useCashfreeCreateOrderMutation()
  const { launch, isLaunching: isLaunchingNetbanking } = useLaunchGuard()

  const launchNetbanking = async ({
    amount,
    source,
    isTrial = false,
  }: LaunchNetbankingParams): Promise<void> => {
    const result = await launch(async () => {
      track(
        AnalyticsEvents.RECHARGE_INITIATED,
        { amount, method: 'netbanking', is_trial: isTrial, gateway: 'cashfree', source },
        // sendBeacon: the _self redirect tears the page down, and a normal XHR
        // would be cancelled in flight.
        { transport: 'sendBeacon' },
      )

      return runCashfreeNetbankingCheckout({
        accountId: getAccountId(),
        amount,
        // Not analytics-only: this puts upgrade_plan on the order, which is the only
        // thing that upgrades a free-trial account paying by netbanking.
        isTrial,
        createOrder: createCashfreeOrder,
        launchCheckout: launchCashfreeHostedCheckout,
      })
    })

    // Undefined = the guard suppressed a duplicate click, which is not a failure.
    // Success leaves the guard latched; the page is navigating away.
    if (!result || result.ok) return

    logger.error('useCashfreeNetbankingCheckout: netbanking launch failed', {
      reason: result.reason,
      error: result.error,
      source,
    })
    errorToast({
      title: 'Payment Failed',
      // A thrown error may carry the gateway's own message; the guard cases
      // (no amount / account / session) have none, so they take the fallback.
      description: result.error ? apiErrorMessage(result.error, FAILURE_MESSAGE) : FAILURE_MESSAGE,
    })
  }

  return { launchNetbanking, isLaunchingNetbanking }
}
